<script setup lang="ts">
// NPM
import { onBeforeMount, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import useVuelidate from '@vuelidate/core';
import { createI18nMessage, url } from '@vuelidate/validators';
import { isValidPhoneNumber } from 'libphonenumber-js';

// Components
import {
  BaseLoader,
  FieldInput,
  FieldPhoneNumber,
  BaseButton,
} from '@magma-app/magma-lapilli';

// Stores
import { useUserStore } from '@/stores/user';
import { useCommonStore } from '@/stores/common';
import { useAsyncAction } from '@/composables/useAsyncAction';
import { zodEmailValidator } from '@/utils/emailValidation';
import {
  isDiscordTouchpoint,
  isValidDiscordTouchpointValue,
  normalizeDiscordTouchpointValue,
} from '@/utils/discord';
const userStore = useUserStore();
const commonStore = useCommonStore();
const { isRunning: isSaving, run } = useAsyncAction();

// Plugins
const { t } = useI18n({ useScope: 'global' });
const withI18nMessage = createI18nMessage({ t });

// Touchpoint URL prefixes (similar to ambassador-signup touchpoints.vue)
const touchPointsPrefix: { [key: string]: string | null } = {
  instagram: 'https://www.instagram.com/',
  facebook: 'https://www.facebook.com/',
  'X (Twitter)': 'https://www.twitter.com/',
  telegram: 'https://t.me/',
  tiktok: 'https://www.tiktok.com/@',
  youtube: 'https://www.youtube.com/channel/',
  weChat: 'https://weixin.qq.com/',
  linkedin: 'https://linkedin.com/in/',
  discord: null,
  calendar: null,
  whatsapp: null,
  line: 'https://line.me/',
  phone: null,
  messenger: 'https://m.me/',
  snapchat: 'https://www.snapchat.com/add/',
  calendly: null,
  littleredbook: null,
};

const touchPointsPlaceholder: { [key: string]: string } = {
  instagram: 'https://www.instagram.com/username',
  facebook: 'https://www.facebook.com/username',
  'X (Twitter)': 'https://www.twitter.com/username',
  telegram: 'https://t.me/username',
  tiktok: 'https://www.tiktok.com/@username',
  youtube: 'https://www.youtube.com/channel/channelid',
  weChat: 'https://weixin.qq.com/username',
  linkedin: 'https://linkedin.com/in/username',
  discord: 'username',
  calendar: 'https://calendar.app/username',
  whatsapp: '+33606060606',
  line: 'https://line.me/username',
  phone: '+33606060606',
  messenger: 'https://m.me/username',
  snapchat: 'https://www.snapchat.com/add/username',
  calendly: 'https://calendly.com/username',
  littleredbook: 'https://www.xiaohongshu.com/user/profile/userid',
};

// Reactive variables
const initialized = ref(false);
const formData = reactive({} as { [key: string]: string });
const formDataSaved = reactive({ ...formData });
const orderedTouchpoints = ref<typeof userStore.userTouchpoints>([]);

// Form validation
const formRules = reactive({} as { [key: string]: object });
const watchFields = ref<string[]>([]);
const v$ = useVuelidate(formRules, formData);

// Lifecycle hooks
onBeforeMount(async () => {
  await userStore.getUserTouchpoints();

  if (userStore.userTouchpoints) {
    // Order touchpoints: filled ones first, then empty ones
    const touchpointsWithValues: typeof userStore.userTouchpoints = [];
    const touchpointsWithoutValues: typeof userStore.userTouchpoints = [];

    userStore.userTouchpoints.forEach((touchpoint) => {
      const touchpointValue = isDiscordTouchpoint(touchpoint.name)
        ? normalizeDiscordTouchpointValue(touchpoint.userTouchPointValue)
        : touchpoint.userTouchPointValue;

      if (touchpointValue && touchpointValue.trim()) {
        touchpointsWithValues.push(touchpoint);
        formData[touchpoint.name] = touchpointValue;
        formDataSaved[touchpoint.name] = touchpointValue;
      } else {
        touchpointsWithoutValues.push(touchpoint);
      }

      // Set up validation rules for all touchpoints
      if (touchpoint.type === 'phoneNumber') {
        formRules[touchpoint.name] = {
          validPhoneNumber: withI18nMessage(
            (value: string) => {
              if (value != '') {
                return isValidPhoneNumber(value);
              } else {
                return true;
              }
            },
            {
              messagePath: () =>
                t('generic.phoneNumber') + t('validation.valid'),
            }
          ),
        };
      } else if (touchpoint.type === 'email') {
        formRules[touchpoint.name] = {
          email: withI18nMessage(zodEmailValidator, {
            messagePath: () => t('validation.invalidEmail'),
          }),
        };
      } else if (touchpoint.type === 'pseudo') {
        formRules[touchpoint.name] = isDiscordTouchpoint(touchpoint.name)
          ? {
              discordUsername: withI18nMessage(isValidDiscordTouchpointValue, {
                messagePath: () => t('validation.urlValidation'),
              }),
            }
          : {
              url: withI18nMessage(url, {
                messagePath: () => t('validation.urlValidation'),
              }),
            };
      }

      watchFields.value.push(touchpoint.name);
    });

    // Set the ordered touchpoints (filled first, then empty)
    orderedTouchpoints.value = [
      ...touchpointsWithValues,
      ...touchpointsWithoutValues,
    ];
  }

  // Set up watchers
  if (v$.value) {
    watchFields.value.forEach((fieldName) => {
      watch(
        () => formData[fieldName],
        async () => {
          if (v$.value && v$.value[fieldName]) {
            await v$.value[fieldName].$validate();
          } else {
            console.error(`Validation for ${fieldName} is not defined.`);
          }
        }
      );
    });
  }

  initialized.value = true;
});

// Methods
const hasChanges = () => {
  if (!userStore.userTouchpoints) return false;

  return userStore.userTouchpoints.some((touchpoint) => {
    const currentValue = formData[touchpoint.name] || '';
    const savedValue = formDataSaved[touchpoint.name] || '';
    return currentValue !== savedValue;
  });
};

// Smart input sanitization based on touchpoint type (similar to ambassador-signup)
const sanitizeInput = (value: string, touchpointName: string): string => {
  const trimmed = value.trim();
  const prefix = touchPointsPrefix[touchpointName];

  if (isDiscordTouchpoint(touchpointName)) {
    return normalizeDiscordTouchpointValue(trimmed);
  }

  // If input already contains the full URL and has the expected prefix, return as-is
  if (prefix && trimmed.startsWith(prefix)) {
    return trimmed;
  }

  // If input contains a full URL but with wrong prefix, extract the username part
  if (
    prefix &&
    (trimmed.startsWith('http://') || trimmed.startsWith('https://'))
  ) {
    try {
      const url = new URL(trimmed);
      const pathname = url.pathname.replace(/^\/+/, ''); // Remove leading slashes
      return pathname || trimmed; // If no pathname, keep original
    } catch {
      return trimmed;
    }
  }

  // Handle @ symbols for platforms like TikTok, Instagram, etc.
  if (prefix && trimmed.startsWith('@')) {
    return trimmed.substring(1); // Remove @ symbol, platform prefix will add proper URL
  }

  // For URL type touchpoints without prefix, validate as URL
  if (!prefix && touchPointsPlaceholder[touchpointName] === 'url') {
    if (
      !trimmed.startsWith('http://') &&
      !trimmed.startsWith('https://') &&
      trimmed.length > 0
    ) {
      return `https://${trimmed}`;
    }
  }

  return trimmed;
};

// Generate final URL for backend
const generateFinalUrl = (value: string, touchpointName: string): string => {
  const sanitized = sanitizeInput(value, touchpointName);

  // If the sanitized value is empty, return empty string (don't add prefix)
  if (!sanitized || sanitized.trim() === '') {
    return '';
  }

  const prefix = touchPointsPrefix[touchpointName];

  if (prefix && !sanitized.startsWith('http')) {
    return `${prefix}${sanitized}`;
  }

  return sanitized;
};

// Helper function to get placeholder for touchpoint
const getTouchpointPlaceholder = (touchpoint: {
  name: string;
  type: string;
}): string => {
  if (touchpoint.type === 'email') {
    return `email@${t('generic.example')}.com`;
  }

  return (
    touchPointsPlaceholder[touchpoint.name] ||
    'https://www.example.com/username'
  );
};

const saveAllTouchpoints = () => run(async () => {
  if (!v$.value || !hasChanges()) return;

  let hasErrors = false;
  const touchpointsToUpdate: Array<{ touchPointId: number; value: string }> =
    [];

  // Validate and collect only changed touchpoints
  for (const touchpoint of userStore.userTouchpoints || []) {
    const touchpointName = touchpoint.name;
    const currentValue = formData[touchpointName] || '';
    const savedValue = formDataSaved[touchpointName] || '';

    // Only process if this touchpoint has changes
    if (currentValue !== savedValue) {
      if (v$.value[touchpointName]) {
        v$.value[touchpointName].$touch();

        if (!(await v$.value[touchpointName].$validate())) {
          hasErrors = true;
          continue;
        }
      }

      // Generate the final URL with proper prefix
      const finalValue = generateFinalUrl(currentValue, touchpointName);

      touchpointsToUpdate.push({
        touchPointId: touchpoint.id,
        value: finalValue,
      });
    }
  }

  if (hasErrors) {
    return;
  }

  // Only make API calls for changed touchpoints
  if (touchpointsToUpdate.length === 0) {
    return;
  }

  try {
    await Promise.all(
      touchpointsToUpdate.map(({ touchPointId, value }) =>
        userStore.updateUserTouchpoint({ touchPointId, value })
      )
    );

    // Update saved values only for the changed ones
    touchpointsToUpdate.forEach(({ touchPointId }) => {
      const touchpoint = userStore.userTouchpoints?.find(
        (t) => t.id === touchPointId
      );
      if (touchpoint) {
        formDataSaved[touchpoint.name] = formData[touchpoint.name];
      }
    });

    // Show success toast notification
    commonStore.setAlert({
      open: true,
      type: 'success',
      title: t('generic.informationUpdated'),
    });
  } catch (error) {
    console.log(error);
  }
});
</script>

<template>
  <template v-if="initialized">
    <div class="flex flex-row gap-8 items-start justify-between">
      <div class="flex flex-col gap-2 w-2/3">
        <h2 class="text-2xl font-bold leading-7 text-foreground-emphasis">
          {{ $t('settingsTouchpointsView.touchpointsHeading') }}
        </h2>
        <p class="text-sm text-foreground-emphasis">
          {{ $t('settingsTouchpointsView.touchpointsDescription') }}
        </p>
      </div>
      <!-- IF THERE'S NO MODIFICATIONS: DISABLE THE BUTTON -->
      <BaseButton
        :text="$t('generic.save')"
        size="md"
        class="w-1/3"
        :disabled="isSaving || !hasChanges()"
        @click="saveAllTouchpoints"
      />
    </div>

    <div
      v-for="touchpoint of orderedTouchpoints"
      :key="touchpoint.id"
      class="mt-5 flex items-center justify-between"
    >
      <div class="flex items-center text-sm text-foreground-emphasis w-1/3">
        <img class="mr-3 h-6 w-6" :src="touchpoint.icon" />
        <span>{{ touchpoint.name }}</span>
      </div>

      <FieldPhoneNumber
        v-if="touchpoint.type === 'phoneNumber'"
        :id="touchpoint.name"
        v-model="formData[touchpoint.name]"
        class="w-2/3"
        :locale="$i18n.locale"
        placeholder="06 06 06 06 06"
        :validation="v$[touchpoint.name]"
      />

      <FieldInput
        v-else
        :id="touchpoint.name"
        v-model="formData[touchpoint.name]"
        class="w-2/3"
        :role="touchpoint.type === 'email' ? 'email' : 'default'"
        :type="
          touchpoint.type === 'email'
            ? 'email'
            : touchpoint.type === 'url'
              ? 'url'
              : 'text'
        "
        :locale="$i18n.locale"
        :placeholder="getTouchpointPlaceholder(touchpoint)"
        :validation="v$[touchpoint.name]"
      />
    </div>
  </template>

  <BaseLoader v-else class="min-h-screen" />
</template>
